// Turns an audience configuration's raw filter conditions into a readable,
// business-speak narrative for the Makeup screen. Pure (no React) so it can be
// unit-tested and reused by any view. We deliberately never surface raw column
// or table names — transaction fields are mapped to marketer language, and
// anything unmapped (notably user-attribute ids, whose human labels live in
// ClickHouse, not in state) falls back to a humanized form.
//
// A transaction audience has TWO levels, and the narrative keeps them distinct:
//   1. Transaction filter (`TransactionDefinition`) — what makes a single
//      purchase match (field conditions).
//   2. User behavior (`UserTransactionBehavior`) — which people to target, by
//      the purchases they've made (built from level-1 patterns).
// At BOTH levels the must/should/must_not buckets mean the same thing, so we
// translate by EFFECTIVE LOGIC rather than the raw key:
//   - must     → required (AND): all must be present.
//   - should   → "at least one of" (OR), but it is a HARD requirement, not a
//                preference; a lone `should` is identical to a `must`, so it
//                folds into the required group. Only a 2+ `should` reads as a
//                distinct "any of" group.
//   - must_not → excluded (a negated OR / NOR): none may be present; matching
//                ANY one excludes the record/user.

import type {
    FieldValue,
    TransactionDefinition,
    UserAttributesDefinition,
    UserGroup,
    UserTransactionBehavior,
} from '@/lib/domain'

// Business-friendly label per transaction dataset field family. Covers the
// user_demographics_intent / cpg_transactions transaction schemas; unknown fields humanize.
const TXN_FIELD_LABELS: Record<string, string> = {
    brand_name: 'Brand',
    store_name: 'Store',
    channel: 'Channel',
    card_type: 'Card type',
    mcc_label: 'Category',
    mcc_general_category: 'Category',
    iab_tier_1: 'Category',
    monetary_score_brand: 'Spend',
    monetary_score_brand_mcc: 'Spend',
    monetary_score_brand_iab: 'Spend',
    frequency_score_brand: 'Purchase frequency',
    frequency_score_brand_mcc: 'Purchase frequency',
    frequency_score_brand_iab: 'Purchase frequency',
    recency_score_brand: 'Recency',
    recency_score_brand_mcc: 'Recency',
    recency_score_brand_iab: 'Recency',
    state: 'Location',
    msa_name: 'Location',
    metro_name: 'Location',
    primary_ruca: 'Area type',
    brand_type: 'Brand type',
    store_type: 'Store type',
}

const titleCase = (s: string): string =>
    s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())

const humanize = (s: string): string => titleCase(s.replaceAll('_', ' '))

// Known coded values mapped to plain words.
const VALUE_LABELS: Record<string, string> = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    online: 'Online',
    'b&m': 'In-store',
    debit: 'Debit',
    credit: 'Credit',
    unknown: 'Unknown',
    true: 'Yes',
    false: 'No',
    yes: 'Yes',
    no: 'No',
}

const formatValue = (v: string): string => {
    const key = v.trim().toLowerCase()
    if (VALUE_LABELS[key]) return VALUE_LABELS[key]
    // ALL-CAPS multiword values (brand / store names) → Title Case.
    if (v === v.toUpperCase() && /[a-z]/i.test(v)) return titleCase(v)
    return v
}

const txnLabel = (field: string): string =>
    TXN_FIELD_LABELS[field] ?? humanize(field)

// --- Effective-logic bucketing ----------------------------------------------

// How a group of conditions/patterns combines, after collapsing the lone-should
// case (see file header).
export type EffectiveKind = 'required' | 'anyOf' | 'excluded'

// Bucket a must/should/must_not triple into effective groups. A lone `should`
// folds into `required` (it is logically identical to a `must`); a 2+ `should`
// becomes its own `anyOf`; `must_not` is `excluded`. Empty groups are dropped,
// and the result is always ordered required → anyOf → excluded.
const effectiveGroups = <T>(
    must: T[],
    should: T[],
    mustNot: T[]
): { kind: EffectiveKind; items: T[] }[] => {
    const required = should.length === 1 ? [...must, ...should] : must
    const anyOf = should.length >= 2 ? should : []
    const groups: { kind: EffectiveKind; items: T[] }[] = []
    if (required.length) groups.push({ kind: 'required', items: required })
    if (anyOf.length) groups.push({ kind: 'anyOf', items: anyOf })
    if (mustNot.length) groups.push({ kind: 'excluded', items: mustNot })
    return groups
}

// --- Level 1: transaction filter (field conditions) -------------------------

export interface NarrativeCondition {
    label: string
    values: string[]
}

export interface ConditionBucket {
    kind: EffectiveKind
    conditions: NarrativeCondition[]
}

const conditionOf = (
    fv: FieldValue,
    labelFor: (field: string) => string
): NarrativeCondition => ({
    label: labelFor(fv.field),
    values: (fv.values ?? []).map(formatValue),
})

// A transaction pattern's (`TransactionDefinition`) field conditions, bucketed by
// effective logic (required / any-of / excluded) using txn field labels.
export const definitionNarrative = (
    definition: TransactionDefinition | undefined,
    labelFor: (field: string) => string = humanize
): ConditionBucket[] => {
    if (!definition) return []
    const valid = (fvs: FieldValue[] | undefined): FieldValue[] =>
        (fvs ?? []).filter((fv) => fv?.field)
    return effectiveGroups(
        valid(definition.must),
        valid(definition.should),
        valid(definition.must_not)
    ).map((g) => ({
        kind: g.kind,
        conditions: g.items.map((fv) => conditionOf(fv, labelFor)),
    }))
}

// The tall user-attribute table's structural primary-key columns. A `UserGroup`
// constrains these to pin one (attribute, value): `ab_column_name` selects WHICH
// attribute, `attribute_value` its value. Mirrors the backend `UserAttributeDataset`
// field defaults and the literal column names in insight_service's marginal scan.
// Hard-coded because agent state carries only the FieldValue codes — not the
// dataset's field metadata (`is_attribute_primary_key`) nor the human attribute
// labels (those live in the dataset's `field_name` / the insight charts). A dataset
// using different structural columns simply won't collapse (falls back, below).
const ATTRIBUTE_KEY_COLUMN = 'ab_column_name'
const ATTRIBUTE_VALUE_COLUMN = 'attribute_value'

// One user-attribute group's conditions, narrated. The canonical group pins an
// attribute (`ab_column_name` = <id>) at one or more values (`attribute_value` =
// <…>); collapse that into a SINGLE condition — labelled by the (humanized)
// attribute id with the value(s) as the chip — instead of leaking the raw
// `ab_column_name` / `attribute_value` column names as two separate conditions. Any
// other (non-structural) conditions render normally; a group that doesn't pin a
// single attribute falls back entirely to per-condition rendering.
const attributeGroupConditions = (
    group: UserGroup,
    labelFor: (field: string) => string
): NarrativeCondition[] => {
    const valid = (group?.conditions ?? []).filter((c) => c?.field)
    const keyCond = valid.find((c) => c.field === ATTRIBUTE_KEY_COLUMN)
    const valueCond = valid.find((c) => c.field === ATTRIBUTE_VALUE_COLUMN)
    if (!keyCond || !valueCond || keyCond.values?.length !== 1) {
        return valid.map((c) => conditionOf(c, labelFor))
    }
    const pinned: NarrativeCondition = {
        label: labelFor(keyCond.values[0]),
        values: (valueCond.values ?? []).map(formatValue),
    }
    const extras = valid
        .filter(
            (c) =>
                c.field !== ATTRIBUTE_KEY_COLUMN &&
                c.field !== ATTRIBUTE_VALUE_COLUMN
        )
        .map((c) => conditionOf(c, labelFor))
    return [pinned, ...extras]
}

// A user-attribute definition narrates like a transaction behavior's
// `patternBuckets`: bucket the GROUPS by effective logic (must → required, 2+
// should → "any of", must_not → excluded), each group narrated as one "pattern".
// Unlike a transaction pattern, a `UserGroup` is a FLAT conjunction — its
// `conditions` all AND on one tall row — so each group yields a single `required`
// inner bucket, with the structural (attribute, value) pair collapsed to one
// readable condition (see `attributeGroupConditions`). Empty groups are dropped so
// they don't render as blank boxes or skew the lone-should fold. The common case
// (one group in `must`) yields one required bucket with one pattern, rendered inline.
export const attributeNarrative = (
    definition: UserAttributesDefinition | undefined,
    labelFor: (field: string) => string = humanize
): PatternBucket[] => {
    if (!definition) return []
    const groups = (gs: UserGroup[] | undefined): PatternNarrative[] =>
        (gs ?? [])
            .map((g): PatternNarrative => {
                const conditions = attributeGroupConditions(g, labelFor)
                return {
                    buckets: conditions.length
                        ? [{ kind: 'required', conditions }]
                        : [],
                }
            })
            .filter((p) => p.buckets.length > 0)
    return effectiveGroups(
        groups(definition.must),
        groups(definition.should),
        groups(definition.must_not)
    ).map((g) => ({ kind: g.kind, patterns: g.items }))
}

// --- Level 2: user behavior (which transactions a user made) ----------------

// One transaction pattern (a `TransactionDefinition`), narrated.
export interface PatternNarrative {
    buckets: ConditionBucket[]
}

// Patterns bucketed by effective logic at the user level.
export interface PatternBucket {
    kind: EffectiveKind
    patterns: PatternNarrative[]
}

// One transaction behavior: its plain-English description plus the pattern
// buckets (which purchases the user must / at-least-one-of / must-not have made).
export interface BehaviorNarrative {
    description?: string
    patternBuckets: PatternBucket[]
}

const behaviorNarrative = (
    behavior: UserTransactionBehavior
): BehaviorNarrative => {
    // Narrate each pattern, dropping any that carry no conditions so empty
    // patterns don't skew the lone-should fold or render as blank boxes.
    const patterns = (
        defs: TransactionDefinition[] | undefined
    ): PatternNarrative[] =>
        (defs ?? [])
            .map((def) => ({ buckets: definitionNarrative(def, txnLabel) }))
            .filter((p) => p.buckets.length > 0)
    const patternBuckets = effectiveGroups(
        patterns(behavior.must),
        patterns(behavior.should),
        patterns(behavior.must_not)
    ).map((g) => ({ kind: g.kind, patterns: g.items }))
    return {
        description: behavior.description?.trim() || undefined,
        patternBuckets,
    }
}

// All behaviors for a transaction (or crossed) config. Users match ANY behavior
// (OR). Behaviors with neither a description nor any patterns are dropped.
export const transactionNarrative = (
    behaviors: UserTransactionBehavior[] | undefined
): BehaviorNarrative[] =>
    (behaviors ?? [])
        .map(behaviorNarrative)
        .filter((b) => b.patternBuckets.length > 0 || b.description)

// --- Presentation labels (the views read these) -----------------------------

// User-behavior level: what the person must have done. `count` distinguishes the
// "all of" / "any of" wording when a group has more than one pattern.
export const userBucketLabel = (kind: EffectiveKind, count: number): string => {
    switch (kind) {
        case 'required':
            return count > 1 ? 'Bought all of' : 'Bought'
        case 'anyOf':
            return 'Bought any of'
        case 'excluded':
            return count > 1 ? 'Never bought any of' : 'Never bought'
    }
}

// User-attribute group level: how the attribute groups combine. Mirrors
// `userBucketLabel` but in attribute-matching language (a group is a set of
// attribute conditions, not a purchase). `count` tunes the "all of" wording.
export const attributeGroupLabel = (
    kind: EffectiveKind,
    count: number
): string => {
    switch (kind) {
        case 'required':
            return count > 1 ? 'Match all of' : 'Match'
        case 'anyOf':
            return 'Match any of'
        case 'excluded':
            return count > 1 ? 'Exclude any of' : 'Exclude'
    }
}

// Field level (inside a transaction pattern / a user-attribute definition).
// Required conditions are listed plainly (no heading); the others are flagged.
export const fieldBucketLabel = (kind: EffectiveKind): string | null => {
    switch (kind) {
        case 'required':
            return null
        case 'anyOf':
            return 'Any of'
        case 'excluded':
            return 'Excluding'
    }
}

export const BUCKET_COLOR: Record<EffectiveKind, string> = {
    required: 'teal',
    anyOf: 'blue',
    excluded: 'red',
}
